import './styles.css'
import React from 'react'
import {
  Button,
  Dropdown,
  Icon,
  Input,
  Label,
  Message,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from 'semantic-ui-react'
import { formatBookingsTableRows, makeDateFormatter } from '../../utils/tableDataFormatter'
import { URL_PATHS } from '../../../../../constants'
import useSessionStorageState from '../../../../../common-components/hooks/useSessionStorage'

const getTableCellClassName = key => {
  let className = ''
  if (key === 'venue_location_state') {
    className = 'wordBreakAll'
  }
  return className
}

const getTableCellTitle = (booking, column) => {
  const value = column.render ? column.render(booking) : ''
  if (typeof value === 'string') return value
  return ''
}

const ALL_COLUMNS = (translate, collectorsList) => [
  {
    key: 'booking_reference_id',
    label: translate('Reference #'),
    isSortable: true,
    render: b => b.bookingReferenceId,
  },
  // Optional “Created By”
  {
    key: 'created_by',
    label: translate('createdBy'),
    isSortable: false,
    dependsOn: 'createdBy',
    render: b => b.bookingCreatedBy,
  },
  {
    key: 'venue_name',
    label: translate('Venue Name'),
    isSortable: true,
    render: b => b.customerName,
  },
  // Optional “State”
  {
    key: 'venue_location_state',
    label: translate('State'),
    isSortable: true,
    dependsOn: 'state',
    render: b => b.state,
  },
  {
    key: 'postcode',
    label: translate('Postcode'),
    isSortable: true,
    render: b => b.postalCode,
  },
  {
    key: 'preferred_pickup',
    label: translate('Preferred Pickup'),
    isSortable: false,
    render: b => b.preferredPickup,
  },
  {
    key: 'date_requested',
    label: translate('Date Requested'),
    isSortable: true,
    render: b => b.dateRequested,
  },
  {
    key: 'keg_quantity',
    label: translate('Quantity'),
    isSortable: true,
    render: b => b.kegQuantity,
  },
  // Optional “Assigned By”
  {
    key: 'assigned_by',
    label: translate('assignedBy'),
    isSortable: false,
    dependsOn: 'assignedBy',
    render: b => b.bookingAssignedBy || <Icon name={'minus'} style={{ color: 'grey' }} />,
  },
  // Optional “Assigned Collector”
  {
    key: 'assigned_collector',
    label: translate('assignedCollector'),
    isSortable: false,
    dependsOn: 'assignedCollector',
    // eslint-disable-next-line
    render: b => {
      return b.collectorCustomerName ? (
        <span style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <span style={{ fontWeight: '600' }}>{b.collectorCustomerName}</span>
          <span style={{ fontSize: '12px' }}>{`Tap ID: ${b.collectorCustomerTapId}`}</span>
        </span>
      ) : (
        <Icon name={'minus'} style={{ color: 'grey' }} />
      )
    },
  },
  // Optional “Preferred Collector”
  {
    key: 'preferred_collector',
    label: translate('preferredCollector'),
    isSortable: false,
    dependsOn: 'preferredCollector',
    // eslint-disable-next-line
    render: b => {
      const found = collectorsList.find(coll => coll.id === b.preferredCollectorId)
      if (found) return found.name
      return <Icon name={'minus'} style={{ color: 'grey' }} />
    },
  },
  {
    key: 'estimated_pickup_date_range',
    label: translate('estimatedPickupDateRange'),
    isSortable: true,
    render: b => {
      const startEstimatedPickupDate = b.startEstimatedPickupDate
      const endEstimatedPickupDate = b.endEstimatedPickupDate
      if (startEstimatedPickupDate && endEstimatedPickupDate) {
        return `${startEstimatedPickupDate} - ${endEstimatedPickupDate}`
      }
      return <Icon name={'minus'} style={{ color: 'grey' }} />
    },
  },
  {
    key: 'booking_status',
    label: translate('Status'),
    isSortable: true,
    render: b => {
      const status = b.bookingStatus?.toUpperCase?.() || '—'
      let className = ''
      switch (status) {
        case 'PENDING':
          className = 'statusBadgePending'
          break
        case 'ASSIGNED':
          className = 'statusBadgeAssigned'
          break
        case 'SCHEDULED':
          className = 'statusBadgeScheduled'
          break
        case 'COMPLETED':
          className = 'statusBadgeCompleted'
          break
        case 'CANCELLED':
          className = 'statusBadgeCancelled'
          break
        default:
          className = 'statusBadgeUnknown'
          break
      }
      return <span className={`statusBadge ${className}`}>{translate(status)}</span>
    },
  },
]

const clamp = (n, min, max) => Math.min(Math.max(n, min), max)

const SESSION_KEYS = {
  sortKey: 'kegCollectionBookings.table.sortKey',
  sortDirection: 'kegCollectionBookings.table.sortDirection',
}

const KegCollectionBookingsTable = props => {
  const {
    bookingsList,
    translate,
    noResultsMessage,
    history,
    dispatch,
    createAction,
    saveSingleBookingActionType,
    fetchSingleBookingSuccessActionType,
    pageNumber,
    pageSize,
    totalElements,
    totalPages,
    dispatchBookingsAPIParams,
    extraFieldsEnabled,
    isExtraFilterEnabled,
    collectorsList,
  } = props

  const locale = navigator.language || undefined
  const dateFormatter = React.useMemo(() => makeDateFormatter(locale), [locale])

  const tableData = React.useMemo(
    () =>
      bookingsList.length ? formatBookingsTableRows(bookingsList, translate, dateFormatter) : [],
    [bookingsList, translate, dateFormatter]
  )

  const [storedSortKey, setStoredSortKey] = useSessionStorageState({
    key: SESSION_KEYS.sortKey,
    initialValue: 'date_requested',
    version: '1',
  })

  const [storedSortDirection, setStoredSortDirection] = useSessionStorageState({
    key: SESSION_KEYS.sortDirection,
    initialValue: 'descending',
    version: '1',
  })

  const allColumns = React.useMemo(
    () => ALL_COLUMNS(translate, collectorsList.content),
    [translate, collectorsList.content]
  )

  const visibleColumns = React.useMemo(() => {
    if (!extraFieldsEnabled || extraFieldsEnabled.length === 0) {
      return allColumns.filter(col => !col.dependsOn)
    }
    return allColumns.filter(col => !col.dependsOn || extraFieldsEnabled.includes(col.dependsOn))
  }, [allColumns, extraFieldsEnabled])

  // eslint-disable-next-line prefer-arrow-callback
  const TableRowItem = React.memo(function TableRowItem({ booking, onClick, columns }) {
    const handleKeyDown = React.useCallback(
      e => {
        if (e.key === 'Enter') {
          onClick()
        }
      },
      [onClick]
    )

    return (
      <TableRow
        className="bookingTableRowClickable"
        onClick={onClick}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {columns.map(col => (
          <TableCell
            key={col.key}
            className={getTableCellClassName(col.key)}
            title={getTableCellTitle(booking, col)}
          >
            {typeof col.render === 'function' ? col.render(booking) : null}
          </TableCell>
        ))}
      </TableRow>
    )
  })

  const PAGE_SIZE_OPTIONS = React.useMemo(
    () => [
      { key: 10, text: translate('bookingsTableFooter10Rows'), value: 10 },
      { key: 30, text: translate('bookingsTableFooter30Rows'), value: 30 },
      { key: 50, text: translate('bookingsTableFooter50Rows'), value: 50 },
      { key: 100, text: translate('bookingsTableFooter100Rows'), value: 100 },
      { key: 300, text: translate('bookingsTableFooter300Rows'), value: 300 },
      { key: 500, text: translate('bookingsTableFooter500Rows'), value: 500 },
    ],
    [translate]
  )

  const [jumpPage, setJumpPage] = React.useState('')
  const [jumpError, setJumpError] = React.useState('')

  const validateJump = React.useCallback(
    raw => {
      if (raw === '') return { ok: false, msg: '' }
      if (!/^\d+$/.test(raw))
        return { ok: false, msg: translate?.('numbersOnly') || 'Numbers only' }
      const n = parseInt(raw, 10)
      if (n < 1 || n > Number(totalPages)) {
        return { ok: false, msg: translate?.('outOfRange') || 'Out of range' }
      }
      return { ok: true, msg: '' }
    },
    [totalPages, translate]
  )

  const handleJumpChange = React.useCallback(
    (e, { value }) => {
      setJumpPage(value)
      const v = validateJump(value)
      setJumpError(v.ok ? '' : v.msg)
    },
    [validateJump]
  )

  const submitJump = React.useCallback(() => {
    const v = validateJump(jumpPage)
    if (!v.ok) return
    const target = clamp(parseInt(jumpPage, 10), 1, Number(totalPages))
    if (target !== Number(pageNumber)) {
      dispatchBookingsAPIParams({ type: 'PAGE_NUMBER', value: target })
    }
  }, [jumpPage, totalPages, pageNumber, dispatchBookingsAPIParams, validateJump])

  const onJumpKeyDown = React.useCallback(
    e => {
      if (e.key === 'Enter') {
        e.preventDefault()
        submitJump()
      }
    },
    [submitJump]
  )

  const handleSort = React.useCallback(
    key => {
      setStoredSortKey(key)
      setStoredSortDirection(prev => {
        if (prev === 'ascending') return 'descending'
        return 'ascending'
      })
    },
    [setStoredSortKey, setStoredSortDirection]
  )

  const handleRedirectToBookingDetails = React.useCallback(
    booking => {
      const { bookingId, customerName } = booking
      dispatch(createAction(saveSingleBookingActionType, booking))
      dispatch(createAction(fetchSingleBookingSuccessActionType))
      history.push(`${URL_PATHS.employeeKegCollectionBookings.path}/${bookingId}`, {
        bookingBreadCrumbLabel: customerName,
      })
    },
    [
      history,
      dispatch,
      createAction,
      saveSingleBookingActionType,
      fetchSingleBookingSuccessActionType,
    ]
  )

  const handlePageChange = React.useCallback(
    (e, { activePage }) => {
      dispatchBookingsAPIParams({ type: 'PAGE_NUMBER', value: activePage })
    },
    [dispatchBookingsAPIParams]
  )

  const handlePageSizeChange = React.useCallback(
    (e, { value }) => {
      dispatchBookingsAPIParams({ type: 'PAGE_SIZE', value })
      dispatchBookingsAPIParams({ type: 'PAGE_NUMBER', value: 1 })
    },
    [dispatchBookingsAPIParams]
  )

  const { rangeStart, rangeEnd } = React.useMemo(() => {
    if (!totalElements || totalElements === 0) {
      return { rangeStart: 0, rangeEnd: 0 }
    }
    const start = (Number(pageNumber) - 1) * Number(pageSize) + 1
    const end = Math.min(Number(pageNumber) * Number(pageSize), Number(totalElements))
    return { rangeStart: start, rangeEnd: end }
  }, [pageNumber, pageSize, totalElements])

  const rangeText = React.useMemo(() => {
    const format = n => (n != null ? Number(n).toLocaleString(locale) : '')
    return `${format(rangeStart)} - ${format(rangeEnd)} ${translate('of')} ${format(totalElements)}`
  }, [rangeStart, rangeEnd, totalElements, translate, locale])

  const sortedData = React.useMemo(() => {
    if (!tableData.length) return tableData
    const key = storedSortKey || 'date_requested'
    const direction = storedSortDirection || 'descending'
    const directionMultiplier = direction === 'ascending' ? 1 : -1

    const rows = [...tableData]
    rows.sort((a, b) => {
      const referenceIdA = a?.bookingReferenceId || ''
      const referenceIdB = b?.bookingReferenceId || ''
      switch (key) {
        case 'venue_name':
          return a._sort_customerName.localeCompare(b._sort_customerName) * directionMultiplier
        case 'keg_quantity':
          return (a._sort_kegQuantity - b._sort_kegQuantity) * directionMultiplier
        case 'date_requested':
          return (a._sort_dateRequestedTs - b._sort_dateRequestedTs) * directionMultiplier
        case 'estimated_pickup_date_range':
          return (
            (a._sort_startEstimatedPickupDateTs - b._sort_startEstimatedPickupDateTs) *
            directionMultiplier
          )
        case 'venue_location_state':
          return a._sort_state.localeCompare(b._sort_state) * directionMultiplier
        case 'postcode':
          return a._sort_postcode.localeCompare(b._sort_postcode) * directionMultiplier
        case 'booking_status':
          return a._sort_bookingStatus.localeCompare(b._sort_bookingStatus) * directionMultiplier
        case 'booking_reference_id':
          return referenceIdA.localeCompare(referenceIdB) * directionMultiplier
        default:
          return 0
      }
    })
    return rows
  }, [tableData, storedSortKey, storedSortDirection])

  const isTableCloseToOverflow = React.useMemo(
    () => isExtraFilterEnabled('preferredCollector') && isExtraFilterEnabled('assignedCollector'),
    [isExtraFilterEnabled]
  )

  return (
    <div className="animate-into-view-1">
      <Table
        sortable
        compact
        selectable
        textAlign="center"
        className={`sticky-header-table ${isTableCloseToOverflow ? 'table-overflow-fix' : ''}`}
      >
        <TableHeader>
          <TableRow>
            {visibleColumns.map(({ key, label, isSortable }) => (
              <TableHeaderCell
                key={key}
                scope="col"
                onClick={isSortable ? () => handleSort(key) : undefined}
                sorted={isSortable && storedSortKey === key ? storedSortDirection : null}
                className={isSortable ? 'bookingColumnIsSortable' : 'notSortable'}
                title={translate(label)}
              >
                {translate(label)}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        {sortedData.length > 0 && (
          <TableBody>
            {sortedData.map(b => (
              <TableRowItem
                key={b.bookingId}
                rowKey={b.bookingId}
                booking={b}
                onClick={() => handleRedirectToBookingDetails(b)}
                columns={visibleColumns}
              />
            ))}
          </TableBody>
        )}
      </Table>

      {sortedData.length === 0 && (
        <div id="bookingsTableNoResults">
          <Message content={translate(noResultsMessage)} />
        </div>
      )}

      <div id="bookingsTableFooter">
        <div id="bookingsTableFooterLeft">
          <div id="bookingsTableFooterLeftContent">
            <Label basic color="grey" id="bookingsTableFooterLeftText">
              {rangeText}
            </Label>
          </div>
        </div>

        <div id="bookingsTableFooterRight">
          <div id="bookingsTableFooterRightContent">
            <Input
              id="bookingsTableGoToPageInput"
              type="number"
              min={1}
              max={totalPages}
              value={jumpPage}
              placeholder={sortedData.length === 0 ? 0 : pageNumber?.toString()}
              disabled={sortedData.length === 0 || totalPages <= 1}
              onChange={handleJumpChange}
              onKeyDown={onJumpKeyDown}
              size="small"
              style={{ width: '120px' }}
              action={
                <Button
                  size="small"
                  onClick={submitJump}
                  disabled={
                    jumpPage === '' ||
                    !!jumpError ||
                    Number.isNaN(Number(jumpPage)) ||
                    parseInt(jumpPage, 10) === Number(pageNumber) ||
                    sortedData.length === 0 ||
                    totalPages <= 1
                  }
                  aria-label={'Go to page'}
                >
                  {translate('Go')}
                </Button>
              }
              label={{
                basic: true,
                content: translate('Page'),
              }}
              labelPosition="left"
            />
            {jumpError && (
              <Label
                id="bookingsTableGoToPageInputError"
                basic
                color="red"
                pointing
                htmlFor="bookingsTableGoToPageInput"
              >
                {jumpError} ({translate('1 to')} {totalPages})
              </Label>
            )}
          </div>
          <Pagination
            id="bookingsTablePagination"
            disabled={sortedData.length === 0 || totalPages <= 1}
            defaultActivePage={pageNumber}
            ellipsisItem={{ content: <Icon name="ellipsis horizontal" />, icon: true }}
            firstItem={{ content: <Icon name="angle double left" />, icon: true }}
            lastItem={{ content: <Icon name="angle double right" />, icon: true }}
            prevItem={{ content: <Icon name="angle left" />, icon: true }}
            nextItem={{ content: <Icon name="angle right" />, icon: true }}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
          <div id="bookingsTablePageSizeDropdownContainer">
            <Dropdown
              id="bookingsTablePageSizeDropDwn"
              fluid
              selection
              disabled={sortedData.length === 0}
              options={PAGE_SIZE_OPTIONS}
              value={pageSize}
              onChange={handlePageSizeChange}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default React.memo(KegCollectionBookingsTable)
